/**
 * Copy aprovada dos estados de falha de integração de ocorrência na grade de
 * Posição de Notas Fiscais (TMS-8396 fase 2 — ADR-0002, decisão D2).
 *
 * As strings abaixo são LITERAIS e foram conferidas uma a uma contra
 * `.dev-agents/state/TMS-8396-fase2-decisoes-copy.json`, que é autoridade do
 * usuário. Não parafrasear, não ajustar pontuação, não trocar travessão (—) por
 * hífen, não "melhorar" o texto.
 *
 * Procedência de cada redação:
 *  - E1 e E2 — copy aprovada original.
 *  - E3 — decisão Q1 do usuário para o título; o corpo é a string que já está no
 *    ar em `src/views/InclusaoOcorrenciaDetalhes.vue:461`, promovida de tooltip a
 *    texto visível, capitalizada e terminada (ADR-0002 D6). Zero palavra nova.
 *  - E4a — decisão Q2 do usuário, sem o trecho `ao longo de {janela}`, removido
 *    pelo próprio contrato de decisões (`ajuste_E4.problema_1`): a janela exigiria
 *    a data da PRIMEIRA tentativa, que não existe no payload.
 *  - E4b — `ajuste_E4.E4b_sem_contador` do contrato de decisões, com a acentuação
 *    restaurada. NÃO é a paráfrase do contrato do Architect de frontend; a
 *    divergência foi arbitrada pelo Tech Lead em favor do contrato do usuário.
 *
 * A interpolação dos `{placeholders}` mora em
 * `src/utils/ocorrenciaIntegracaoEstado.js`, não aqui: quem audita a copy lê
 * strings, quem audita a regra lê funções.
 *
 * Precedente de produção desta forma de guardar texto aprovado:
 * `src/constants/fornecedorPlataforma.js`.
 */

/* -------------------------------------------------------------------------- */
/* E1 — transitória: a retentativa automática está agendada                    */
/* -------------------------------------------------------------------------- */

export const E1_TITULO = "Erro — reenvio automático em andamento";

export const E1_CORPO =
  "{motivoIntegracaoLabel} em {dtUltimaTentativaIntegracao}. O iSend vai tentar de novo sozinho; não é preciso abrir chamado.";

/** Sem a data da última tentativa (janela entre a inscrição e a 1ª tentativa). */
export const E1_CORPO_SEM_DATA =
  "{motivoIntegracaoLabel}. O iSend vai tentar de novo sozinho; não é preciso abrir chamado.";

/** Sem motivo: a oração que o citaria cai inteira e o resto segue literal. */
export const E1_CORPO_SEM_MOTIVO =
  "O iSend vai tentar de novo sozinho; não é preciso abrir chamado.";

/* -------------------------------------------------------------------------- */
/* E2 — permanente, com motivo diagnosticado                                   */
/* -------------------------------------------------------------------------- */

export const E2_TITULO = "Erro — precisa de correção antes de reenviar";

/** A segunda frase é literal e imutável. */
export const E2_CORPO =
  "{motivoIntegracaoLabel} Reenviar não resolve enquanto a causa não for corrigida.";

/* -------------------------------------------------------------------------- */
/* E3 — sem motivo preservado (627.880 ocorrências, 99,5% do passivo)          */
/* -------------------------------------------------------------------------- */

export const E3_TITULO = "Erro na integração";

export const E3_CORPO =
  "Esta ocorrência entrou em erro antes do registro do motivo da falha.";

/* -------------------------------------------------------------------------- */
/* E4a — tentativas automáticas esgotadas, com contador                        */
/* -------------------------------------------------------------------------- */

export const E4A_TITULO = "Erro — tentativas automáticas esgotadas";

export const E4A_CORPO =
  "O iSend tentou reenviar {tentativasIntegracao} vezes e a comunicação com o rastreamento (iTrack) seguiu falhando. Última tentativa em {dtUltimaTentativaIntegracao}.";

/** Sem a data da última tentativa: a frase inteira que a citaria é omitida. */
export const E4A_CORPO_SEM_DATA =
  "O iSend tentou reenviar {tentativasIntegracao} vezes e a comunicação com o rastreamento (iTrack) seguiu falhando.";

/* -------------------------------------------------------------------------- */
/* E4b — sem contador: as 454 legadas transitórias, nunca inscritas na fila    */
/* -------------------------------------------------------------------------- */

export const E4B_TITULO = "Erro — sem reenvio automático em andamento";

export const E4B_CORPO =
  "Falha de comunicação com o rastreamento (iTrack). Este registro não está na fila de reenvio automático; reenviar agora é a ação disponível.";

/* -------------------------------------------------------------------------- */
/* Ações                                                                        */
/* -------------------------------------------------------------------------- */

export const ACAO_REENVIAR = "Reenviar agora";

/**
 * O rótulo é literal do precedente da squad (`Expedicao.vue`, TMS-7247). Ele não
 * abre chamado: copia o bloco preenchido para a área de transferência. Não existe
 * integração com Service Desk neste repositório (ADR-0002 D5).
 */
export const ACAO_ABRIR_CHAMADO = "Abrir chamado com estes dados";

export const COPIA_SUCESSO_TITULO = "Copiado";
export const COPIA_SUCESSO_MENSAGEM =
  "Dados copiados. Cole no chamado do Service Desk.";

export const COPIA_FALHA_TITULO = "Erro";
export const COPIA_FALHA_MENSAGEM =
  "Não foi possível copiar. Abra Detalhes técnicos, selecione o texto e copie manualmente.";

/* -------------------------------------------------------------------------- */
/* Tooltip do botão "Reenviar" desabilitado                                    */
/* Porte literal de InclusaoOcorrenciaDetalhes.vue:457-468 — texto NÃO reescrito */
/* -------------------------------------------------------------------------- */

export const REENVIO_INDISPONIVEL_PREFIXO = "Reenvio indisponível: ";
export const REENVIO_INDISPONIVEL_SUFIXO =
  ". Reenviar não altera esta falha — a causa precisa ser corrigida antes de uma nova tentativa.";
export const REENVIO_INDISPONIVEL_MOTIVO_AUSENTE =
  "esta ocorrência entrou em erro antes do registro do motivo da falha";

/* -------------------------------------------------------------------------- */
/* Bloco copiado para a área de transferência                                  */
/* Rótulos operacionais, no molde de `bloqueioExpedicaoTextoParaChamado`       */
/* (Expedicao.vue, branch TMS-7247). Não fazem parte da copy aprovada de tela. */
/* -------------------------------------------------------------------------- */

export const CHAMADO_TITULO = "Falha de integração de ocorrência no iSend";
export const CHAMADO_DETALHES_TITULO = "Detalhes técnicos:";
export const VALOR_AUSENTE = "não informado";

/* -------------------------------------------------------------------------- */
/* Rótulos do bloco "Detalhes técnicos"                                        */
/* -------------------------------------------------------------------------- */

export const ROTULO_CODIGO_OCORRENCIA = "Código da ocorrência";
export const ROTULO_ID_OCORRENCIA = "ID da ocorrência";
export const ROTULO_CARGA = "Carga";
export const ROTULO_NOTA = "Nota";
export const ROTULO_MOTIVO = "Motivo";
export const ROTULO_HORARIO_OCORRENCIA = "Horário da ocorrência";
export const ROTULO_HORARIO_ULTIMA_TENTATIVA = "Horário da última tentativa";
export const ROTULO_NUMERO_TENTATIVA = "Número da tentativa";
export const ROTULO_NUMERO_TENTATIVAS = "Número de tentativas";
export const ROTULO_RESPOSTA_ITRACK = "Resposta da iTrack";
export const ROTULO_TIPO_FALHA = "Tipo de falha";

/* -------------------------------------------------------------------------- */
/* Índices por estado — consumidos pelo helper                                 */
/* -------------------------------------------------------------------------- */

export const TITULOS_POR_ESTADO = Object.freeze({
  E1: E1_TITULO,
  E2: E2_TITULO,
  E3: E3_TITULO,
  E4a: E4A_TITULO,
  E4b: E4B_TITULO,
});
